'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { normalizeToE164 } from '@/lib/utils/phone-validation';
import { Plus, X, Copy, Check } from 'lucide-react';

/**
 * Invite-by-email section, mounted in BOTH the routine detail "Add members"
 * dialog and the creation wizard's members step. Controlled: the parent owns
 * the entry rows ({@link InviteEntry}) and submits them to
 * POST /api/routines/[routineId]/members/invite; per-row results come back as
 * {@link InviteRowOutcome} and are rendered here as outcome badges.
 *
 * The parsing/validation helpers below are PURE and exported for unit tests —
 * they client-side-mirror the server's parseInviteEmails contract (split on
 * comma/space/newline, trim, lowercase, dedupe).
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Client-side mirror of ROUTINE_INVITE_BATCH_CAP
 * (lib/services/routine-invite-service.ts — a server module this client bundle
 * must not import). The invite endpoint 400s above this many rows per request,
 * so callers cap entry lists and chunk oversized batches to this size.
 */
export const INVITE_BATCH_CAP = 25;

export interface InviteEntry {
  /** Stable render key (not sent to the server). */
  key: string;
  email: string;
  name: string;
  phone: string;
}

/** Per-row result statuses from POST /api/routines/[routineId]/members/invite. */
export type InviteRowStatus =
  | 'added'
  | 'added_to_domain'
  | 'provisioned'
  // 'invited_pending' is the current pending shape (carries a reason); the older
  // 'seat_exhausted_pending' is kept for back-compat so a stale/rolling deploy
  // still renders correctly. Both mean "invitation sent, they'll join on accept".
  | 'invited_pending'
  | 'seat_exhausted_pending'
  // PR 2.0: a phone-first member (name + phone, no email) — render `phone`,
  // never the placeholder `email`; their welcome is a text, not an email.
  | 'added_by_phone'
  | 'invalid'
  | 'failed';

/** Why a row is invited_pending — drives the reason-aware sub-line. */
export type InvitePendingReason = 'seats_full' | 'needs_verification';

export interface InviteRowOutcome {
  email: string;
  status: InviteRowStatus;
  /** PR 2.0: the E.164 of a phone-first row (added_by_phone / bad_phone). */
  phone?: string;
  userId?: string;
  /** Present on the pending statuses — classic invite link to share manually. */
  inviteUrl?: string;
  /**
   * On invited_pending: 'seats_full' | 'needs_verification' (drives the
   * sub-line). On invalid: 'bad_email'/'invalid_email' | 'bad_phone'/'invalid_phone'.
   * On failed: free-form message.
   */
  reason?: string;
}

/** Both reason spellings are live across the seam — treat them uniformly. */
export function isBadPhoneReason(reason: string | undefined): boolean {
  return reason === 'bad_phone' || reason === 'invalid_phone';
}

/**
 * The two pending outcomes — an invitation was sent and the person joins once
 * they accept. NON-terminal (see isInviteSuccessStatus): the dialog stays open
 * so the owner sees the badge + copy-link, and the row is ACTIONED (locked from
 * re-submit) rather than pruned.
 */
export const INVITE_PENDING_STATUSES: ReadonlySet<InviteRowStatus> = new Set([
  'invited_pending',
  'seat_exhausted_pending',
]);

export function isInvitePendingStatus(status: string): boolean {
  return INVITE_PENDING_STATUSES.has(status as InviteRowStatus);
}

/** Statuses that mean "this person is now on the routine" (dialog may close). */
export const INVITE_SUCCESS_STATUSES: ReadonlySet<InviteRowStatus> = new Set([
  'added',
  'added_to_domain',
  'provisioned',
  'added_by_phone',
]);

export function isInviteSuccessStatus(status: string): boolean {
  return INVITE_SUCCESS_STATUSES.has(status as InviteRowStatus);
}

/**
 * A row whose action is COMPLETE and must not be re-submitted: either fully on
 * the routine (success) or an invitation already sent (pending). Re-running the
 * fallback for a pending row would rotate its token (dead copied link) and
 * re-email the invitee — so both classes are locked/removed from the
 * re-submittable set after a submit.
 */
export function isInviteActionedStatus(status: string): boolean {
  return isInviteSuccessStatus(status) || isInvitePendingStatus(status);
}

// ─── Pure helpers (exported for tests) ───────────────────────────────────────

/**
 * Parse a pasted blob into clean emails: split on comma/semicolon/whitespace
 * (incl. newlines), trim, lowercase, drop empties, dedupe preserving order.
 */
export function parseInviteEmailsInput(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[\s,;]+/)) {
    const email = piece.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

/** Lightweight client-side validity check (server re-validates). */
export function isValidInviteEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

/**
 * Non-blocking TLD-slip suggestion: `.con` / `.cm` endings → `.com` ONLY
 * (anything broader — .co, .orq, … — risks flagging real domains).
 * Returns the corrected email, or null when no suggestion applies.
 */
export function suggestEmailFix(email: string): string | null {
  const lower = email.trim().toLowerCase();
  if (!lower.includes('@')) return null;
  const m = lower.match(/^(.+)\.(con|cm)$/);
  return m ? `${m[1]}.com` : null;
}

/**
 * Shape entries for the invite endpoint body: non-empty emails only
 * (trimmed/lowercased), optional trimmed name, phone normalized to E.164 when
 * possible (raw otherwise — the server flags bad_phone per row).
 */
export function buildInvitePayloadEntries(
  entries: Array<Pick<InviteEntry, 'email' | 'name' | 'phone'>>,
): Array<{ email: string; name?: string; phone?: string }> {
  const seen = new Set<string>();
  const out: Array<{ email: string; name?: string; phone?: string }> = [];
  for (const e of entries) {
    const email = e.email.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    const row: { email: string; name?: string; phone?: string } = { email };
    const name = e.name.trim();
    if (name) row.name = name;
    const phoneRaw = e.phone.trim();
    if (phoneRaw) row.phone = normalizeToE164(phoneRaw) ?? phoneRaw;
    out.push(row);
  }
  return out;
}

let entryKeySeq = 0;
export function makeInviteEntry(email: string): InviteEntry {
  entryKeySeq += 1;
  return { key: `invite-${entryKeySeq}-${email}`, email, name: '', phone: '' };
}

// ─── Outcome badge ───────────────────────────────────────────────────────────

/** Reason-aware sub-line under a pending badge — why they aren't in yet. */
function pendingSubLine(reason: string | undefined): string {
  if (reason === 'needs_verification') return 'we emailed them to confirm it’s really them';
  // 'seats_full' or a legacy seat_exhausted_pending (no reason) → seats message.
  return 'no seats free';
}

function OutcomeBadge({ outcome }: { outcome: InviteRowOutcome }) {
  switch (outcome.status) {
    case 'added':
      return <Badge variant="success">Added</Badge>;
    case 'added_to_domain':
      return <Badge variant="success">Added to team</Badge>;
    case 'provisioned':
      return <Badge variant="success">Account created — welcome email sent</Badge>;
    case 'added_by_phone':
      return <Badge variant="success">Added by phone — they&apos;ll get a text from this number</Badge>;
    case 'invited_pending':
    case 'seat_exhausted_pending':
      return <Badge variant="warning">Invite sent — they’ll join when they accept</Badge>;
    case 'invalid':
      return (
        <Badge variant="warning">
          {isBadPhoneReason(outcome.reason) ? 'Invalid phone' : 'Invalid email'}
        </Badge>
      );
    default:
      return (
        <Badge variant="destructive" title={outcome.reason}>
          Failed
        </Badge>
      );
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

interface InviteByEmailSectionProps {
  entries: InviteEntry[];
  onChange: (entries: InviteEntry[]) => void;
  /** Per-row results from the last submit; rendered as a badge list below the rows. */
  outcomes?: InviteRowOutcome[] | null;
  disabled?: boolean;
}

export function InviteByEmailSection({
  entries,
  onChange,
  outcomes,
  disabled,
}: InviteByEmailSectionProps) {
  const [pasteValue, setPasteValue] = useState('');
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const addFromPaste = () => {
    const emails = parseInviteEmailsInput(pasteValue);
    if (emails.length === 0) return;
    const existing = new Set(entries.map((e) => e.email.trim().toLowerCase()));
    const additions = emails.filter((e) => !existing.has(e)).map(makeInviteEntry);
    if (additions.length > 0) onChange([...entries, ...additions]);
    setPasteValue('');
  };

  const updateEntry = (key: string, patch: Partial<Omit<InviteEntry, 'key'>>) => {
    onChange(entries.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  };

  const removeEntry = (key: string) => {
    onChange(entries.filter((e) => e.key !== key));
  };

  const handlePhoneBlur = (entry: InviteEntry) => {
    const raw = entry.phone.trim();
    if (!raw) return;
    const normalized = normalizeToE164(raw);
    if (normalized && normalized !== entry.phone) {
      updateEntry(entry.key, { phone: normalized });
    }
  };

  const copyInviteUrl = async (email: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('[InviteByEmail] Copy failed', err);
    }
  };

  return (
    <div className="space-y-3">
      {/* Paste box — comma / space / newline separated bulk entry */}
      <div className="flex gap-2">
        <Input
          value={pasteValue}
          onChange={(e) => setPasteValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addFromPaste();
            }
          }}
          placeholder="Paste emails — comma, space or newline separated"
          disabled={disabled}
          className="text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 h-10"
          onClick={addFromPaste}
          disabled={disabled || parseInviteEmailsInput(pasteValue).length === 0}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>

      {/* Entry rows */}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry) => {
            const email = entry.email.trim().toLowerCase();
            const invalid = email !== '' && !isValidInviteEmail(email);
            const suggestion = suggestEmailFix(email);
            return (
              <div key={entry.key} className="space-y-1">
                {/* Wraps at <sm so all three inputs stay usable on a 375px
                    viewport: email takes its own line, name+phone+remove share
                    the next. At sm+ the classes collapse to the original
                    single-line layout (flex-1 email, fixed name/phone). */}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={entry.email}
                    onChange={(e) => updateEntry(entry.key, { email: e.target.value })}
                    placeholder="email@company.com"
                    disabled={disabled}
                    aria-invalid={invalid}
                    className={cn(
                      'w-full min-w-0 text-sm sm:w-auto sm:flex-1',
                      invalid && 'border-amber-500 focus-visible:ring-amber-500',
                    )}
                  />
                  <Input
                    value={entry.name}
                    onChange={(e) => updateEntry(entry.key, { name: e.target.value })}
                    placeholder="Name (optional)"
                    disabled={disabled}
                    className="flex-1 min-w-0 text-sm sm:flex-none sm:w-32"
                  />
                  <Input
                    value={entry.phone}
                    onChange={(e) => updateEntry(entry.key, { phone: e.target.value })}
                    onBlur={() => handlePhoneBlur(entry)}
                    placeholder="+1 604 …"
                    disabled={disabled}
                    className="w-28 shrink-0 text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeEntry(entry.key)}
                    disabled={disabled}
                    aria-label={`Remove ${entry.email || 'row'}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {invalid && !suggestion && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    This doesn&apos;t look like an email address.
                  </p>
                )}
                {suggestion && (
                  <button
                    type="button"
                    className="text-xs rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400"
                    onClick={() => updateEntry(entry.key, { email: suggestion })}
                    disabled={disabled}
                  >
                    Did you mean {suggestion}?
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Per-row outcomes from the last submit */}
      {outcomes && outcomes.length > 0 && (
        <div className="space-y-1.5 rounded-md border p-2.5 bg-muted/30">
          {outcomes.map((o) => {
            const pending = isInvitePendingStatus(o.status);
            // A phone-first row is a person, not a mailbox: show the number.
            const label = o.status === 'added_by_phone' || (!o.email && o.phone) ? o.phone ?? o.email : o.email;
            return (
              <div key={o.email || o.phone} className="space-y-0.5">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-muted-foreground">{label}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <OutcomeBadge outcome={o} />
                    {pending && o.inviteUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 gap-1 px-1.5 text-xs text-muted-foreground"
                        onClick={() => copyInviteUrl(o.email, o.inviteUrl!)}
                        title="Copy invite link"
                      >
                        {copiedEmail === o.email ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                        {copiedEmail === o.email ? 'Copied' : 'Copy link'}
                      </Button>
                    )}
                  </span>
                </div>
                {pending && (
                  <p className="text-xs text-muted-foreground pl-0.5">
                    {pendingSubLine(o.reason)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
